import { Component, OnInit, Inject, PLATFORM_ID, ViewChild, ElementRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { CandidatoCvService, CandidatoCvDTO } from 'src/app/services/candidato-cv/candidato-cv.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SeoService } from 'src/app/services/seo/seo.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { ScrapedJobService, ScrapedJob } from 'src/app/services/scraped-job/scraped-job.service';
import { CvOptimizerService, CvAnalysis } from 'src/app/services/cv-optimizer/cv-optimizer.service';
import { ConsentService } from 'src/app/services/consent/consent.service';

@Component({
  selector: 'app-cv-vault',
  templateUrl: './cv-vault.component.html',
  styleUrls: ['./cv-vault.component.scss']
})
export class CvVaultComponent implements OnInit {

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  cvs: CandidatoCvDTO[] = [];
  cargando = false;
  subiendo = false;
  isDragOver = false;
  errorMsg = '';
  successMsg = '';
  isPremium = false;
  alertasActivas = false;
  freeGuruMatchVisible = 3;
  esBienvenida = false;

  labelInput = '';
  archivoSeleccionado: File | null = null;
  previewName = '';

  candidatoId: number;
  readonly MAX_CVS_PREMIUM = 5;
  readonly MAX_CVS_FREE = 1;
  readonly MAX_SIZE_MB = 5;

  get MAX_CVS(): number {
    return this.isPremium ? this.MAX_CVS_PREMIUM : this.MAX_CVS_FREE;
  }

  // ── GuruMatch results ─────────────────────────────────────────────────────
  matchedVacantes: ScrapedJob[] = [];

  matchScores: Map<string, number> = new Map();
  matchJustifications: Map<string, string> = new Map();
  matchSuggestions: Map<string, string> = new Map();
  guruMatchAt: string | null = null;
  guruMatchLoading = false;
  guruMatchLimitado = false;
  guruMatchUsosRestantes: number | null = null;
  guruMatchEjecutado = false;
  historyLoading = false;
  optimizerLoadingCv: Set<number> = new Set();
  optimizerResults: Map<number, CvAnalysis> = new Map();

  get activeCvId(): number | null {
    return this.cvs.find(c => c.isActive)?.id ?? null;
  }

  constructor(
    public cvService: CandidatoCvService,
    private authService: AuthService,
    private seo: SeoService,
    private alertService: AlertService,
    private scrapedJobService: ScrapedJobService,
    private cvOptimizerService: CvOptimizerService,
    private route: ActivatedRoute,
    private consent: ConsentService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.seo.setDefault('Mis CVs', 'Gestiona tus currículums y elige cuál presentar a cada vacante.');
    this.esBienvenida = this.route.snapshot.queryParamMap.get('bienvenido') === '1';
  }

  ngOnInit(): void {
    const user = this.authService.currentUserValue;
    this.candidatoId = user?.user?.idUsuario;
    if (this.candidatoId) {
      this.cargarCvs();
      // Sincroniza consent — si no aceptó, abrirá modal antes de cualquier acción.
      this.consent.syncFromServer().subscribe();
      this.consent.changes().subscribe(active => {
        if (active === true) {
          if (this.errorMsg === 'Debes aceptar el aviso de privacidad y términos para subir tu CV.') {
            this.errorMsg = '';
            if (this.archivoSeleccionado && this.labelInput.trim() && !this.subiendo) {
              this.subirCv();
            }
          }
        }
      });
      this.alertService.getEstado(this.candidatoId).subscribe({
        next: res => {
          const d = res?.data ?? res;
          this.isPremium = d?.isPremium ?? false;
          this.alertasActivas = !!(d?.correoSet || d?.whatsappOptIn);
          this.freeGuruMatchVisible = d?.freeGuruMatchVisible ?? 3;
        },
        error: () => {}
      });
      this.cargarHistorialMatch();
    }
  }

  cargarHistorialMatch(): void {
    this.historyLoading = true;
    this.scrapedJobService.guruMatchHistory(this.candidatoId).subscribe({
      next: (res) => {
        this.historyLoading = false;
        const history = res?.data;
        if (!history || !history.items || history.items.length === 0) return;
        this._applyMatchResults(history.items, history.matchedAt);
      },
      error: () => { this.historyLoading = false; }
    });
  }

  private _applyMatchResults(items: any[], matchedAt: string): void {
    this.matchScores = new Map(items.map((m: any) => [m.scrapedJobId, m.score]));
    this.matchJustifications = new Map(items.map((m: any) => [m.scrapedJobId, m.justification ?? '']));
    this.matchSuggestions = new Map(items.map((m: any) => [m.scrapedJobId, m.suggestion ?? '']));
    this.guruMatchAt = matchedAt
      ? new Date(matchedAt).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : new Date().toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    this.matchedVacantes = items.map((m: any) => ({
      id: m.scrapedJobId,
      titulo: m.titulo,
      empresa: m.empresa,
      ubicacion: m.ubicacion,
      modalidad: m.modalidad,
      urlOriginal: m.urlOriginal,
      urlVacante: m.urlVacante,
      categoriaNombre: m.categoriaNombre,
      fechaPublicacion: m.fechaPublicacion,
      tecnologias: m.tecnologias ?? [],
      fuente: '',
      idCategoria: 0,
      status: 'PUBLICADO',
      scrapedAt: '',
    } as any));
    this.guruMatchEjecutado = true;
  }

  cargarCvs(): void {
    this.cargando = true;
    this.cvService.list(this.candidatoId).subscribe({
      next: res => { this.cvs = res?.data ?? []; this.cargando = false; },
      error: ()  => { this.cargando = false; }
    });
  }

  // ── Drag & Drop ───────────────────────────────────────────────────────────

  onDragOver(e: DragEvent): void {
    e.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.isDragOver = false;
    const file = e.dataTransfer?.files?.[0];
    if (file) this.procesarArchivo(file);
  }

  onFileSelect(e: Event): void {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) this.procesarArchivo(file);
  }

  private procesarArchivo(file: File): void {
    this.errorMsg = '';
    if (file.type !== 'application/pdf') {
      this.errorMsg = 'Solo se aceptan archivos PDF.';
      return;
    }
    if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
      this.errorMsg = `El archivo no debe superar ${this.MAX_SIZE_MB} MB.`;
      return;
    }
    this.archivoSeleccionado = file;
    this.previewName = file.name;
    if (!this.labelInput) {
      this.labelInput = file.name.replace('.pdf', '');
    }
  }

  // ── Upload ────────────────────────────────────────────────────────────────

  subirCv(): void {
    if (!this.archivoSeleccionado) { this.errorMsg = 'Selecciona un archivo PDF.'; return; }
    if (!this.labelInput.trim()) { this.errorMsg = 'Escribe un nombre para este CV.'; return; }
    if (this.cvs.length >= this.MAX_CVS) {
      this.errorMsg = `Límite de ${this.MAX_CVS} CVs alcanzado. Elimina uno para continuar.`;
      return;
    }

    // Bloqueo previo: requiere aceptar aviso de privacidad y términos.
    // Si el cache local dice false (o null = aún no se sincronizó), abrir modal y abortar.
    if (this.consent.hasActiveSync() !== true) {
      this.consent.requestModal('cargar_cv');
      this.errorMsg = 'Debes aceptar el aviso de privacidad y términos para subir tu CV.';
      return;
    }

    this.subiendo = true;
    this.errorMsg = '';
    this.cvService.upload(this.candidatoId, this.archivoSeleccionado, this.labelInput.trim())
      .subscribe({
        next: res => {
          const newCv: CandidatoCvDTO = res.data;
          const hadNoActive = !this.cvs.some(c => c.isActive);
          this.cvs.unshift(newCv);
          this.resetForm();
          if (hadNoActive) {
            this.activar(newCv);
          } else {
            this.successMsg = '¡CV subido correctamente!';
            setTimeout(() => this.successMsg = '', 4000);
          }
        },
        error: err => {
          this.errorMsg = err?.error?.message ?? err?.error?.messageError ?? 'Error al subir el archivo.';
        },
        complete: () => { this.subiendo = false; }
      });
  }

  // ── Activate ──────────────────────────────────────────────────────────────

  activar(cv: CandidatoCvDTO): void {
    this.cvService.activate(this.candidatoId, cv.id).subscribe({
      next: () => {
        this.cvs.forEach(c => c.isActive = c.id === cv.id);
        this.successMsg = `"${cv.label}" es ahora tu CV activo.`;
        setTimeout(() => this.successMsg = '', 3500);
      },
      error: (err) => { this.errorMsg = 'No se pudo activar el CV: ' + (err?.error?.message || err?.statusText || err?.status || 'error desconocido'); }
    });
  }

  // ── Download / Open ───────────────────────────────────────────────────────

  descargar(cv: CandidatoCvDTO): void {
    this.cvService.download(this.candidatoId, cv.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = cv.fileName || `${cv.label}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: () => { this.errorMsg = 'No se pudo descargar el CV.'; }
    });
  }

  abrir(cv: CandidatoCvDTO): void {
    this.cvService.download(this.candidatoId, cv.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      },
      error: () => { this.errorMsg = 'No se pudo abrir el CV.'; }
    });
  }

  eliminar(cv: CandidatoCvDTO): void {
    if (!confirm(`¿Eliminar "${cv.label}"? Esta acción no se puede deshacer.`)) return;
    this.cvService.delete(this.candidatoId, cv.id).subscribe({
      next: () => {
        this.cvs = this.cvs.filter(c => c.id !== cv.id);
        this.successMsg = `CV "${cv.label}" eliminado.`;
        setTimeout(() => this.successMsg = '', 3500);
      },
      error: () => { this.errorMsg = 'No se pudo eliminar el CV.'; }
    });
  }

  formatSize(bytes: number): string {
    return this.cvService.formatSize(bytes);
  }

  private resetForm(): void {
    this.archivoSeleccionado = null;
    this.previewName = '';
    this.labelInput = '';
    this.subiendo = false;
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  get slotsRestantes(): number {
    return Math.max(0, this.MAX_CVS - this.cvs.length);
  }

  get tieneActivoCv(): boolean {
    return this.cvs.some(c => c.isActive);
  }

  ejecutarGuruMatch(): void {
    if (!this.candidatoId || this.guruMatchLoading) return;
    this.guruMatchLoading = true;
    this.guruMatchLimitado = false;
    this.scrapedJobService.guruMatchGlobal(this.candidatoId).subscribe({
      next: (res) => {
        const matches: any[] = res?.data ?? [];
        const matchedAt = matches.length > 0 ? matches[0].matchedAt : null;
        this._applyMatchResults(matches, matchedAt);
        this.guruMatchUsosRestantes = res?.usosRestantes ?? null;
        this.guruMatchLoading = false;
      },
      error: (err) => {
        if (err.status === 429) {
          this.guruMatchLimitado = true;
          this.guruMatchUsosRestantes = 0;
        }
        this.guruMatchLoading = false;
      }
    });
  }

  optimizarCv(cv: CandidatoCvDTO): void {
    if (!this.candidatoId) return;
    this.optimizerLoadingCv.add(cv.id);
    this.cvOptimizerService.analyze(this.candidatoId, cv.id).subscribe({
      next: (res) => {
        const result = res?.data ?? res;
        const newMap = new Map(this.optimizerResults);
        newMap.set(cv.id, result);
        this.optimizerResults = newMap;
        this.optimizerLoadingCv = new Set(this.optimizerLoadingCv);
        this.optimizerLoadingCv.delete(cv.id);
      },
      error: (err) => {
        const newMap = new Map(this.optimizerResults);
        newMap.set(cv.id, { limitado: true } as any);
        this.optimizerResults = newMap;
        this.optimizerLoadingCv = new Set(this.optimizerLoadingCv);
        this.optimizerLoadingCv.delete(cv.id);
      }
    });
  }

  get topVacantes(): ScrapedJob[] {
    return this.matchedVacantes.filter(v => (this.matchScores.get(v.id) ?? 0) >= 80);
  }

  get lowerVacantes(): ScrapedJob[] {
    return this.matchedVacantes.filter(v => {
      const s = this.matchScores.get(v.id) ?? 0;
      return s >= 70 && s < 80;
    });
  }

  get visibleVacantes(): ScrapedJob[] {
    if (this.isPremium) return this.matchedVacantes;
    return this.matchedVacantes.slice(0, this.freeGuruMatchVisible);
  }

  get blurredVacantes(): ScrapedJob[] {
    if (this.isPremium) return [];
    return this.matchedVacantes.slice(this.freeGuruMatchVisible);
  }

  getScore(id: string): number | null {
    return this.matchScores.has(id) ? (this.matchScores.get(id) as number) : null;
  }

  getJustification(id: string): string {
    return this.matchJustifications.get(id) ?? '';
  }

  getSuggestion(id: string): string {
    return this.matchSuggestions.get(id) ?? '';
  }

  getOptimizerResult(cvId: number): CvAnalysis | null {
    return this.optimizerResults.get(cvId) ?? null;
  }

  get guruMatchFechaRenovacion(): string {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return next.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' });
  }

  scoreColor(score: number): string {
    if (score >= 70) return '#22c55e';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  }

  stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}
